import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { formatErr } from "../../lib/api";
import { Plus, Pencil, Trash2, Coins, Save, Package, Sliders } from "lucide-react";

const CATEGORIES = ["token", "cosmetic", "booster", "frame", "merch"];

const EMPTY_ITEM = {
    name: "",
    description: "",
    price_noex: 100,
    category: "cosmetic",
    image_url: "",
    stock: "",
    active: true,
    payload: "",
};

export default function AdminShop() {
    const [tab, setTab] = useState("items"); // items | rewards | purchases
    const [items, setItems] = useState([]);
    const [config, setConfig] = useState(null);
    const [purchases, setPurchases] = useState([]);
    const [editing, setEditing] = useState(null); // {...item} or EMPTY_ITEM for new

    const loadItems = async () => setItems((await client.get("/admin/shop/items")).data || []);
    const loadConfig = async () => setConfig((await client.get("/admin/rewards/config")).data);
    const loadPurchases = async () => setPurchases((await client.get("/admin/purchases")).data || []);

    useEffect(() => {
        loadItems();
        loadConfig();
        loadPurchases();
    }, []);

    const saveItem = async () => {
        try {
            const body = {
                name: editing.name,
                description: editing.description,
                price_noex: Number(editing.price_noex),
                category: editing.category,
                image_url: editing.image_url || "",
                stock: editing.stock === "" || editing.stock == null ? null : Number(editing.stock),
                active: !!editing.active,
                payload: editing.payload
                    ? (typeof editing.payload === "string" ? JSON.parse(editing.payload || "{}") : editing.payload)
                    : null,
            };
            if (editing.id) {
                await client.patch(`/admin/shop/items/${editing.id}`, body);
                toast.success("Item updated");
            } else {
                await client.post("/admin/shop/items", body);
                toast.success("Item created");
            }
            setEditing(null);
            await loadItems();
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        await client.delete(`/admin/shop/items/${id}`);
        toast.success("Deleted");
        await loadItems();
    };

    const saveConfig = async () => {
        try {
            const body = {
                daily_completed: Number(config.daily_completed),
                weekly_completed: Number(config.weekly_completed),
                challenge_approved: Number(config.challenge_approved),
                monthly_approved: Number(config.monthly_approved),
                quest_completed: Number(config.quest_completed),
                level_up: Number(config.level_up),
                new_pr: Number(config.new_pr),
                streak_milestones: Object.fromEntries(
                    Object.entries(config.streak_milestones || {}).map(([k, v]) => [String(k), Number(v)])
                ),
            };
            await client.put("/admin/rewards/config", body);
            toast.success("Reward config saved");
            await loadConfig();
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    const markPurchase = async (p, status) => {
        const fd = new FormData();
        fd.append("status", status);
        fd.append("shipping_note", p.shipping_note || "");
        await client.patch(`/admin/purchases/${p.id}`, fd);
        toast.success(`Marked ${status}`);
        await loadPurchases();
    };

    return (
        <div data-testid="admin-shop-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="label-cap text-xs text-neon-green">Admin</div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight">Shop &amp; Rewards</h1>
                </div>
            </div>

            <div className="flex gap-2 mb-6 border-b border-charcoal-500">
                {[
                    { k: "items", l: "Items", i: Package },
                    { k: "rewards", l: "Reward Config", i: Sliders },
                    { k: "purchases", l: `Purchases (${purchases.filter((p) => p.status === "pending").length})`, i: Coins },
                ].map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`admin-shop-tab-${t.k}`}
                        className={`label-cap text-xs px-4 py-2 -mb-px border-b-2 transition-all flex items-center gap-1.5 ${
                            tab === t.k ? "border-neon-green text-neon-green" : "border-transparent text-zinc-500 hover:text-white"
                        }`}
                    >
                        <t.i size={14} /> {t.l}
                    </button>
                ))}
            </div>

            {tab === "items" && (
                <div>
                    <button
                        onClick={() => setEditing({ ...EMPTY_ITEM })}
                        data-testid="admin-shop-new-item"
                        className="mb-4 btn-neon px-4 py-2 flex items-center gap-2"
                    >
                        <Plus size={16} /> New Item
                    </button>
                    <div className="grid gap-2">
                        {items.map((it) => (
                            <div key={it.id} className="surface-1 p-3 flex items-center gap-3" data-testid={`admin-item-${it.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="label-cap text-[9px] text-zinc-500">{it.category}</span>
                                        {!it.active && <span className="label-cap text-[9px] text-neon-orange">Inactive</span>}
                                    </div>
                                    <div className="font-bold text-sm truncate">{it.name}</div>
                                    <div className="text-xs text-zinc-500 truncate">{it.description}</div>
                                </div>
                                <div className="text-neon-green font-bold flex items-center gap-1 text-sm">
                                    <Coins size={12} /> {it.price_noex}
                                </div>
                                <button onClick={() => setEditing({ ...it, payload: it.payload ? JSON.stringify(it.payload) : "" })} className="p-2 text-zinc-400 hover:text-neon-green" data-testid={`admin-item-edit-${it.id}`}>
                                    <Pencil size={14} />
                                </button>
                                <button onClick={() => deleteItem(it.id)} className="p-2 text-zinc-400 hover:text-neon-orange" data-testid={`admin-item-delete-${it.id}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === "rewards" && config && (
                <div className="max-w-xl space-y-3" data-testid="admin-reward-form">
                    <p className="text-xs text-zinc-500">
                        Tune how much $NoEx is granted across every earning path. Changes take effect immediately for all future grants.
                    </p>
                    {[
                        ["daily_completed", "Daily Completed"],
                        ["weekly_completed", "Weekly Completed"],
                        ["challenge_approved", "Challenge Approved"],
                        ["monthly_approved", "Monthly Approved"],
                        ["quest_completed", "Quest Completed"],
                        ["level_up", "Level Up Bonus"],
                        ["new_pr", "New PR Bonus"],
                    ].map(([k, l]) => (
                        <div key={k} className="surface-1 p-3 flex items-center gap-3">
                            <label className="flex-1 text-sm">{l}</label>
                            <input
                                type="number"
                                value={config[k] ?? 0}
                                onChange={(e) => setConfig({ ...config, [k]: Number(e.target.value) })}
                                data-testid={`reward-${k}`}
                                className="w-24 bg-charcoal-900 border border-charcoal-500 px-3 py-1.5 text-right"
                            />
                            <span className="text-xs text-neon-green">$NoEx</span>
                        </div>
                    ))}
                    <div className="surface-1 p-3">
                        <div className="label-cap text-xs text-zinc-500 mb-2">Streak Milestones</div>
                        {Object.entries(config.streak_milestones || {}).map(([day, val]) => (
                            <div key={day} className="flex items-center gap-2 mb-1.5">
                                <span className="text-sm w-20">{day}-day:</span>
                                <input
                                    type="number"
                                    value={val}
                                    onChange={(e) => setConfig({
                                        ...config,
                                        streak_milestones: { ...config.streak_milestones, [day]: Number(e.target.value) },
                                    })}
                                    data-testid={`streak-${day}`}
                                    className="flex-1 bg-charcoal-900 border border-charcoal-500 px-3 py-1.5 text-right"
                                />
                                <span className="text-xs text-neon-green">$NoEx</span>
                            </div>
                        ))}
                    </div>
                    <button onClick={saveConfig} data-testid="reward-save" className="btn-neon px-5 py-2 flex items-center gap-2">
                        <Save size={14} /> Save Config
                    </button>
                </div>
            )}

            {tab === "purchases" && (
                <div className="space-y-2" data-testid="admin-purchases-list">
                    {purchases.length === 0 && (
                        <div className="surface-1 p-8 text-center text-zinc-500 text-sm">No purchases yet.</div>
                    )}
                    {purchases.map((p) => (
                        <div key={p.id} className="surface-1 p-3 flex items-center gap-3" data-testid={`admin-purchase-${p.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="label-cap text-[9px] text-zinc-500">{p.item_category}</span>
                                    <span className={`label-cap text-[9px] ${
                                        p.status === "delivered" ? "text-neon-green"
                                        : p.status === "pending" ? "text-neon-orange"
                                        : "text-zinc-500"
                                    }`}>{p.status}</span>
                                </div>
                                <div className="font-bold text-sm truncate">{p.item_name}</div>
                                <div className="text-xs text-zinc-500 truncate">{p.user_name} · {p.user_email}</div>
                                <div className="text-[10px] text-zinc-600">{new Date(p.created_at).toLocaleString()}</div>
                            </div>
                            <div className="text-neon-green font-bold flex items-center gap-1 text-sm">
                                <Coins size={12} /> {p.price_paid}
                            </div>
                            {p.status === "pending" && p.item_category === "merch" && (
                                <button
                                    onClick={() => markPurchase(p, "delivered")}
                                    data-testid={`admin-purchase-deliver-${p.id}`}
                                    className="px-3 py-1.5 text-xs label-cap bg-neon-green text-black hover:opacity-90"
                                >
                                    Mark Delivered
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="surface-2 p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()} data-testid="admin-item-modal">
                        <div className="label-cap text-[10px] text-neon-green">{editing.id ? "Edit" : "New"} Item</div>
                        <input
                            placeholder="Name"
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            data-testid="item-name"
                            className="w-full bg-charcoal-900 border border-charcoal-500 px-3 py-2"
                        />
                        <textarea
                            placeholder="Description"
                            value={editing.description}
                            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                            data-testid="item-description"
                            className="w-full bg-charcoal-900 border border-charcoal-500 px-3 py-2 min-h-[80px]"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                value={editing.category}
                                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                                data-testid="item-category"
                                className="bg-charcoal-900 border border-charcoal-500 px-3 py-2"
                            >
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <input
                                type="number"
                                placeholder="Price $NoEx"
                                value={editing.price_noex}
                                onChange={(e) => setEditing({ ...editing, price_noex: e.target.value })}
                                data-testid="item-price"
                                className="bg-charcoal-900 border border-charcoal-500 px-3 py-2"
                            />
                        </div>
                        <input
                            type="number"
                            placeholder="Stock (empty = unlimited)"
                            value={editing.stock ?? ""}
                            onChange={(e) => setEditing({ ...editing, stock: e.target.value })}
                            data-testid="item-stock"
                            className="w-full bg-charcoal-900 border border-charcoal-500 px-3 py-2"
                        />
                        <input
                            placeholder="Image URL (optional)"
                            value={editing.image_url || ""}
                            onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                            data-testid="item-image"
                            className="w-full bg-charcoal-900 border border-charcoal-500 px-3 py-2"
                        />
                        <input
                            placeholder='Payload JSON e.g. {"code":"frame_neon"}'
                            value={editing.payload || ""}
                            onChange={(e) => setEditing({ ...editing, payload: e.target.value })}
                            data-testid="item-payload"
                            className="w-full bg-charcoal-900 border border-charcoal-500 px-3 py-2 font-mono text-xs"
                        />
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={!!editing.active}
                                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                data-testid="item-active"
                            />
                            Active
                        </label>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setEditing(null)} className="flex-1 surface-1 py-2 label-cap text-xs">Cancel</button>
                            <button onClick={saveItem} data-testid="item-save" className="flex-1 btn-neon py-2">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
